import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { formatDisplayDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileSpreadsheet, Search, Upload, Download, X, Eye, History } from 'lucide-react';
import LifecycleModal from '@/components/LifecycleModal';

export default function AssignmentsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  // Available SIM connections for linking to Mobile assets
  const [simConnections, setSimConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingAsset, setViewingAsset] = useState(null);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [lifecycleAssetId, setLifecycleAssetId] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Filter states
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterEmployees, setFilterEmployees] = useState([]);
  const [filterAssets, setFilterAssets] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '',
    asset_id: '',
    assigned_date: '',
    return_date: '',
    assign_condition: '',
    asset_return_condition: '',
    remarks: '',
    sim_provider: '',
    sim_mobile_number: '',
    sim_type: '',
    sim_ownership: '',
    sim_purpose: '',
    charger_given: true,
    charger_details: '',
    charger_return_note: '',
  });

  // Check if selected asset is Mobile
  const [selectedAssetCategory, setSelectedAssetCategory] = useState('');
  // Category selected in the asset picker UI
  const [selectedAssetPickerCategory, setSelectedAssetPickerCategory] = useState('');
  // Controls open/close state of asset picker popover
  const [assetPopoverOpen, setAssetPopoverOpen] = useState(false);
  // SIM connection picker state
  const [simPopoverOpen, setSimPopoverOpen] = useState(false);
  const [selectedSimProviderFilter, setSelectedSimProviderFilter] = useState('');
  // Selected SIM connection IDs for this assignment (supports multiple SIMs)
  const [selectedSimConnectionIds, setSelectedSimConnectionIds] = useState([]);
  // Pagination: Show 20 / 40 / 60 / 100(/1000) and total count
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    // When coming from global search (openAssignmentId), request a large page once
    location.state?.openAssignmentId || location.state?.openAssignment ? 1000 : 20
  );
  const [totalCount, setTotalCount] = useState(0);
  
  useEffect(() => {
    if (formData.asset_id && assets.length > 0) {
      const asset = assets.find(a => a.asset_id === formData.asset_id);
      const category = asset?.category || '';
      setSelectedAssetCategory(category);
      if (category) {
        setSelectedAssetPickerCategory(category);
      }
      const cat = category.toLowerCase();
      if ((cat === 'laptop' || cat === 'mobile') && !formData.charger_details && asset?.charger) {
        setFormData(prev => ({ ...prev, charger_details: asset.charger }));
      }
    } else {
      setSelectedAssetCategory('');
    }
  }, [formData.asset_id, formData.charger_details, assets]);

  // Auto-fill assign_condition from asset's current condition
  // - When creating: always auto-fill from asset condition
  // - When editing: auto-fill only if assign_condition is currently empty
  useEffect(() => {
    if (formData.asset_id && assets.length > 0 && !formData.assign_condition) {
      const asset = assets.find(a => a.asset_id === formData.asset_id);
      const assetCondition = asset?.condition || asset?.condition_status;
      if (assetCondition && ['New', 'Good', 'Damaged', 'Needs Repair'].includes(assetCondition)) {
        setFormData(prev => ({ ...prev, assign_condition: assetCondition }));
      }
    }
  }, [formData.asset_id, assets, formData.assign_condition]);

  const isMobileAsset = selectedAssetCategory.toLowerCase() === 'mobile';
  const isLaptopOrMobile = selectedAssetCategory.toLowerCase() === 'laptop' || selectedAssetCategory.toLowerCase() === 'mobile';
  const hasReturnDate = !!formData.return_date;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  // When pageSize is 1000 ("All"), hide pagination controls
  const showPagination = pageSize !== 1000 && totalCount > 20;

  const fetchFilterOptions = async () => {
    try {
      const [employeesRes, assetsRes] = await Promise.all([
        api.get('/assignments/employees'),
        api.get('/assignments/assets'),
      ]);
      setFilterEmployees(employeesRes.data);
      setFilterAssets(assetsRes.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedEmployee && selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      if (selectedAsset && selectedAsset !== 'all') {
        params.asset_id = selectedAsset;
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (searchFilter && searchFilter.trim() !== '') {
        params.search = searchFilter.trim();
      }

      // When pageSize is 1000 ("All"), omit page/pageSize so backend returns full list
      if (pageSize !== 1000) {
        params.page = page;
        params.pageSize = pageSize;
      }

      const [assignmentsRes, employeesRes, assetsRes] = await Promise.all([
        api.get('/assignments', { params }),
        api.get('/employees'),
        api.get('/assets'),
      ]);
      const assignmentsData = assignmentsRes.data;

      if (pageSize === 1000) {
        // "All" mode – backend returns plain array without pagination
        const list = Array.isArray(assignmentsData)
          ? assignmentsData
          : assignmentsData?.data || [];
        setAssignments(list);
        setTotalCount(list.length);
      } else {
        if (assignmentsData && typeof assignmentsData === 'object' && 'data' in assignmentsData) {
          setAssignments(assignmentsData.data);
          setTotalCount(assignmentsData.total ?? 0);
        } else {
          setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
          setTotalCount(Array.isArray(assignmentsData) ? assignmentsData.length : 0);
        }
      }
      setEmployees(Array.isArray(employeesRes.data) ? employeesRes.data : (employeesRes.data?.data ?? employeesRes.data?.employees ?? []));
      setAssets(Array.isArray(assetsRes.data) ? assetsRes.data : (assetsRes.data?.data ?? assetsRes.data ?? []));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee, selectedAsset, selectedStatus, pageSize, page, searchFilter]);

  const fetchSimConnections = async () => {
    try {
      // Fetch only available SIMs for assignment dropdown
      const res = await api.get('/sim-connections/available');
      setSimConnections(res.data || []);
    } catch (error) {
      console.error('Failed to fetch SIM connections:', error);
    }
  };

  // Selected asset details for display below picker
  const selectedAssetDetails = assets.find((a) => a.asset_id === formData.asset_id);
  // Selected SIM connection details list (by selected ids from available list)
  const selectedSimDetailsList = simConnections.filter(
    (s) => Array.isArray(selectedSimConnectionIds) && selectedSimConnectionIds.includes(s.id)
  );
  // When editing: show linked SIMs from assignment if they're not in available list (they're already Assigned)
  const selectedSimDetailsForDisplay =
    selectedSimDetailsList.length > 0
      ? selectedSimDetailsList
      : (editingAssignment?.sim_connections || []);

  useEffect(() => {
    fetchFilterOptions();
    fetchSimConnections();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [selectedEmployee, selectedAsset, selectedStatus, pageSize, searchFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = useCallback((assignment = null) => {
    if (assignment) {
      setEditingAssignment(assignment);
      const assignedDateVal = assignment.assigned_date;
      const returnDateVal = assignment.return_date;
      const assignedDateStr = assignedDateVal ? String(assignedDateVal).slice(0, 10) : '';
      const returnDateStr = returnDateVal ? String(returnDateVal).slice(0, 10) : '';
      const matchedAsset = assets.find(a => a.asset_id === assignment.asset_id);
      const chargerGivenFromAssignment =
        assignment.charger_given === true ||
        assignment.charger_given === 't' ||
        assignment.charger_given === 1;
      const inferredChargerGiven =
        chargerGivenFromAssignment ||
        Boolean(assignment.charger_details) ||
        Boolean(assignment.charger_return_note) ||
        Boolean(assignment.charger_return_condition);
      setFormData({
        employee_id: assignment.employee_id,
        asset_id: assignment.asset_id,
        assigned_date: assignedDateStr,
        return_date: returnDateStr,
        assign_condition: assignment.assign_condition || '',
        asset_return_condition: assignment.asset_return_condition || '',
        remarks: assignment.remarks || '',
        sim_provider: assignment.sim_provider || '',
        sim_mobile_number: assignment.sim_mobile_number || '',
        sim_type: assignment.sim_type || '',
        sim_ownership: assignment.sim_ownership || '',
        sim_purpose: assignment.sim_purpose || '',
        charger_given: inferredChargerGiven,
        charger_details: assignment.charger_details || matchedAsset?.charger || '',
        charger_return_note: assignment.charger_return_note || assignment.charger_return_condition || assignment.charger_remarks || '',
      });
    } else {
      setEditingAssignment(null);
      setFormData({
        employee_id: '',
        asset_id: '',
        assigned_date: '',
        return_date: '',
        assign_condition: '',
        asset_return_condition: '',
        remarks: '',
        sim_provider: '',
        sim_mobile_number: '',
        sim_type: '',
        sim_ownership: '',
        sim_purpose: '',
        charger_given: true,
        charger_details: '',
        charger_return_note: '',
      });
    }
    // Reset category picker when opening a fresh dialog
    if (!assignment) {
      setSelectedAssetPickerCategory('');
    }
    // When editing: pre-fill SIM connection selection from linked SIMs; when creating: clear
    setSelectedSimConnectionIds(assignment?.sim_connection_ids ?? []);
    setDialogOpen(true);
  }, [assets]);

  // Open assignment dialog and boost page size when navigated from global search or dashboard
  useEffect(() => {
    const openAssignment = location.state?.openAssignment;
    const openId = location.state?.openAssignmentId;

    // If full assignment object is passed, use it directly
    if (openAssignment) {
      // Show more rows when coming from global search
      if (pageSize < 1000) {
        setPageSize(1000);
        setPage(1);
      }
      handleOpenDialog(openAssignment);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (!openId) return;

    const openFromListOrSearch = async () => {
      if (loading) return;

      // Boost page size once when we know we're opening from global search
      if (pageSize < 1000) {
        setPageSize(1000);
        setPage(1);
      }

      // First, try to find the assignment in the already loaded list
      if (assignments.length > 0) {
        const assignmentFromList = assignments.find(
          (a) => String(a.assignment_id) === String(openId)
        );

        if (assignmentFromList) {
          handleOpenDialog(assignmentFromList);
          navigate(location.pathname, { replace: true, state: {} });
          return;
        }
      }

      // Fallback: search via assignments endpoint so we can open even if it's not on the current page
      try {
        const response = await api.get('/assignments', {
          params: {
            search: openId,
            page: 1,
            pageSize: 1000,
          },
        });

        const res = response.data;
        const list = Array.isArray(res) ? res : res?.data || [];
        const fromSearch = list.find(
          (a) => String(a.assignment_id) === String(openId)
        );

        if (fromSearch) {
          handleOpenDialog(fromSearch);
        }
      } catch (error) {
        console.error('Failed to open assignment from navigation state:', error);
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    };

    openFromListOrSearch();
  }, [assignments, loading, pageSize, location.state?.openAssignment, location.state?.openAssignmentId, location.pathname, navigate, handleOpenDialog]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Asset Return Condition is mandatory if Return Date is filled
    if (formData.return_date && !formData.asset_return_condition) {
      toast.error('Asset Return Condition is required when Return Date is provided');
      return;
    }
    
    try {
      const chargerGivenNormalized =
        formData.charger_given === true ||
        formData.charger_given === 'yes' ||
        formData.charger_given === 'true' ||
        formData.charger_given === 1;
      const chargerDetailsNormalized =
        typeof formData.charger_details === 'string' ? formData.charger_details.trim() : '';
      const hasChargerReturnNote = Boolean(formData.charger_return_note && formData.charger_return_note.trim());
      const payload = {
        ...formData,
        return_date: formData.return_date || null,
        assign_condition: formData.assign_condition || null,
        asset_return_condition: formData.asset_return_condition || null,
        remarks: formData.remarks || null,
        sim_provider: formData.sim_provider || null,
        sim_mobile_number: formData.sim_mobile_number || null,
        sim_type: formData.sim_type || null,
        sim_ownership: formData.sim_ownership || null,
        sim_purpose: formData.sim_purpose || null,
        // Always send charger fields from form state so backend can persist exactly what user selected.
        charger_given: chargerGivenNormalized,
        charger_details: chargerGivenNormalized ? (chargerDetailsNormalized || null) : null,
        // "Charger Returned? (Optional)" is now a free-text field.
        charger_returned: chargerGivenNormalized ? hasChargerReturnNote : null,
        charger_return_date:
          chargerGivenNormalized && hasChargerReturnNote
            ? (formData.return_date || null)
            : null,
        charger_return_condition:
          chargerGivenNormalized && hasChargerReturnNote
            ? (formData.charger_return_note.trim() || null)
            : null,
        charger_remarks: null,
      };

      // For new Mobile asset assignments, send selected SIM connection IDs (supports multiple SIMs)
      if (!editingAssignment && isMobileAsset && selectedSimConnectionIds.length > 0) {
        payload.sim_connection_ids = selectedSimConnectionIds;
      }

      if (editingAssignment) {
        await api.put(`/assignments/${editingAssignment.assignment_id}`, payload);
        toast.success('Assignment updated successfully');
      } else {
        await api.post('/assignments', payload);
        toast.success('Assignment created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleViewAsset = (assignment) => {
    // Find the full asset details from the assets array
    const asset = assets.find(a => a.asset_id === assignment.asset_id);
    if (asset) {
      setViewingAsset({
        ...asset,
        assignment: assignment
      });
      setViewDialogOpen(true);
    } else {
      toast.error('Asset details not found');
    }
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await api.delete(`/assignments/${assignmentId}`);
      toast.success('Assignment deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/assignments/export', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'asset_assignments.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export successful');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/assignments/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'assignments_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/assignments/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Import errors:', response.data.errors);
        toast.warning(`Some rows had errors. Check console for details.`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import assignments');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    try {
      const response = await api.get(`/search/employees?q=${searchQuery}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B1F3A]">Asset Assignments</h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">Track asset allocations</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImport}
            className="hidden"
            data-testid="import-assignments-file-input"
          />
          <Button
            data-testid="download-assignments-template-button"
            variant="outline"
            onClick={handleDownloadTemplate}
            className="border-slate-300 text-xs sm:text-sm"
            size="sm"
          >
            <FileSpreadsheet className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Template</span>
          </Button>
          <Button
            data-testid="import-assignments-button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="border-slate-300 text-xs sm:text-sm"
            size="sm"
          >
            <Upload className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">{importing ? 'Importing...' : 'Import'}</span>
            <span className="sm:hidden">{importing ? '...' : 'Imp'}</span>
          </Button>
          <Button data-testid="search-button" variant="outline" onClick={() => setSearchDialogOpen(true)} className="border-slate-300 text-xs sm:text-sm" size="sm">
            <Search className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Search</span>
          </Button>
          <Button data-testid="export-button" variant="outline" onClick={handleExport} className="border-slate-300 text-xs sm:text-sm" size="sm">
            <Download className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button data-testid="add-assignment-button" onClick={() => handleOpenDialog()} className="bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-xs sm:text-sm" size="sm">
            <Plus className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Assign Asset</span>
            <span className="sm:hidden">Assign</span>
          </Button>
        </div>
      </div>

      {/* Compact Filters Section */}
      <div className="bg-white rounded-lg border border-border p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 w-full sm:min-w-[200px] sm:flex-1">
            <Input
              id="search-assignments"
              placeholder="Search by name, asset, or brand..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setSearchFilter(searchInput.trim());
                }
              }}
              className="h-9 text-sm"
            />
          </div>
          <Select
            value={selectedEmployee || 'all'}
            onValueChange={(value) => setSelectedEmployee(value === 'all' ? 'all' : value)}
          >
            <SelectTrigger id="employee-filter" className="h-9 w-full sm:w-[200px] text-sm">
              <SelectValue placeholder="Employee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {filterEmployees.map((emp) => (
                <SelectItem key={emp.employee_id} value={emp.employee_id}>
                  {emp.employee_name} ({emp.employee_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={selectedAsset || 'all'}
            onValueChange={(value) => setSelectedAsset(value === 'all' ? 'all' : value)}
          >
            <SelectTrigger id="asset-filter" className="h-9 w-full sm:w-[200px] text-sm">
              <SelectValue placeholder="Asset" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assets</SelectItem>
              {filterAssets.map((asset) => (
                <SelectItem key={asset.asset_id} value={asset.asset_id}>
                  {asset.brand ? `${asset.brand} - ` : ''}{asset.asset_name} ({asset.asset_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={selectedStatus || 'all'}
            onValueChange={(value) => setSelectedStatus(value === 'all' ? 'all' : value)}
          >
            <SelectTrigger id="status-filter" className="h-9 w-full sm:w-[160px] text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="returned">Returned</SelectItem>
            </SelectContent>
          </Select>
          {(selectedEmployee !== 'all' || selectedAsset !== 'all' || selectedStatus !== 'all' || searchFilter) && (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground ml-auto">
                <span>{assignments.length} result{assignments.length !== 1 ? 's' : ''}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedEmployee('all');
                  setSelectedAsset('all');
                  setSelectedStatus('all');
                  setSearchFilter('');
                  setSearchInput('');
                }}
                className="h-9 px-3"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">ID</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Employee</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Asset</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Brand</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Assigned Date</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Return Date</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Remarks</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {assignments.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-3 sm:px-6 py-8 text-center text-xs sm:text-sm text-muted-foreground">
                      {(selectedEmployee !== 'all' || selectedAsset !== 'all' || selectedStatus !== 'all' || searchFilter) 
                        ? 'No assignments match the selected filters' 
                        : 'No assignments found'}
                    </td>
                  </tr>
                ) : (
                  assignments.map((assignment) => {
                    const asset = assets.find(a => a.asset_id === assignment.asset_id);
                    const brandName = asset?.brand || '-';
                    
                    return (
                    <tr key={assignment.assignment_id} className="hover:bg-slate-50 transition-colors" data-testid={`assignment-row-${assignment.assignment_id}`}>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">{assignment.assignment_id}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm">
                        <div className="font-medium break-words">{assignment.employee_name}</div>
                        <div className="text-muted-foreground text-xs break-words">{assignment.employee_id}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm">
                        <div className="font-medium break-words">{assignment.asset_name}</div>
                        <div className="text-muted-foreground text-xs break-words">{assignment.asset_id}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm break-words">{brandName}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">{formatDisplayDate(assignment.assigned_date)}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                        {assignment.return_date ? (
                          formatDisplayDate(assignment.return_date)
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm break-words max-w-[150px] truncate">{assignment.remarks || '-'}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                      <div className="flex gap-1 sm:gap-2">
                        <Button
                          data-testid={`view-assignment-${assignment.assignment_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewAsset(assignment)}
                          title="View Asset Details"
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                        <Button
                          data-testid={`lifecycle-assignment-${assignment.assignment_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setLifecycleAssetId(assignment.asset_id);
                            setLifecycleOpen(true);
                          }}
                          title="View Assignment Lifecycle"
                          className="h-8 w-8 p-0"
                        >
                          <History className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                        <Button
                          data-testid={`edit-assignment-${assignment.assignment_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(assignment)}
                          title="Edit Assignment"
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-assignment-${assignment.assignment_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(assignment.assignment_id)}
                          title="Delete Assignment"
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-muted/30 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            {showPagination ? (
              <>
                <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                  Show{' '}
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger className="h-8 w-[70px] text-xs sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                      <SelectItem value="60">60</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="1000">All</SelectItem>
                    </SelectContent>
                  </Select>
                  {' '}| Total Count: <span className="font-semibold text-[#0B1F3A]">{totalCount}</span>
                </p>
                <div className="flex items-center gap-1 text-xs sm:text-sm">
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    &laquo; Previous
                  </Button>
                  <span className="px-2 font-medium text-[#0B1F3A]">{page}/{totalPages}</span>
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                    Next &raquo;
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-xs sm:text-sm font-semibold text-[#0B1F3A]">
                Total: {totalCount} assignment{totalCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{editingAssignment ? 'Edit Assignment' : 'Assign Asset'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="employee_id">Employee</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                  disabled={!!editingAssignment}
                >
                  <SelectTrigger data-testid="assignment-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    {employees.map((emp) => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.full_name} ({emp.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="asset_id">Asset</Label>
                <Popover open={assetPopoverOpen} onOpenChange={setAssetPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between"
                      data-testid="assignment-asset-select"
                      disabled={!!editingAssignment}
                    >
                      <span className="truncate text-left">
                        {formData.asset_id
                          ? assets.find((a) => a.asset_id === formData.asset_id)?.asset_name ||
                            formData.asset_id
                          : 'Select category and asset'}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[520px] p-0" align="start">
                    <div className="text-xs sm:text-sm text-slate-600 px-3 py-2 border-b">
                      First choose a category on the left, then pick an asset on the right.
                    </div>
                    <div className="flex max-h-72">
                      {/* Categories column */}
                      <div className="w-1/3 border-r border-slate-200 bg-slate-50 overflow-y-auto">
                        {Array.from(
                          new Set(
                            assets
                              .filter(
                                (asset) =>
                                  asset.status === 'Available' || asset.asset_id === formData.asset_id
                              )
                              .map((asset) => asset.category)
                              .filter(Boolean)
                          )
                        ).map((category) => (
                          <button
                            key={category}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-xs sm:text-sm border-b last:border-b-0 transition-colors ${
                              selectedAssetPickerCategory === category
                                ? 'bg-[#0B1F3A] text-white'
                                : 'hover:bg-slate-100'
                            }`}
                            onClick={() => {
                              setSelectedAssetPickerCategory(category);
                              if (!editingAssignment) {
                                setFormData((prev) => ({ ...prev, asset_id: '' }));
                              }
                            }}
                            disabled={!!editingAssignment}
                          >
                            {category}
                          </button>
                        ))}
                        {assets.length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500">No assets available</div>
                        )}
                      </div>

                      {/* Assets list column */}
                      <div className="w-2/3 bg-white overflow-y-auto">
                        {assets
                          .filter(
                            (asset) =>
                              (asset.status === 'Available' || asset.asset_id === formData.asset_id) &&
                              (!selectedAssetPickerCategory ||
                                asset.category === selectedAssetPickerCategory)
                          )
                          .map((asset) => {
                            const isSelected = formData.asset_id === asset.asset_id;
                            return (
                              <button
                                key={asset.asset_id}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-xs sm:text-sm border-b last:border-b-0 flex flex-col transition-colors ${
                                  isSelected
                                    ? 'bg-emerald-50 border-l-4 border-emerald-500'
                                    : 'hover:bg-slate-100'
                                }`}
                                onClick={() => {
                                  if (editingAssignment) return;
                                  setFormData((prev) => ({ ...prev, asset_id: asset.asset_id }));
                                  setAssetPopoverOpen(false); // close after selection
                                }}
                                disabled={!!editingAssignment}
                              >
                                <span className="font-medium">
                                  {asset.brand ? `${asset.brand} - ` : ''}
                                  {asset.asset_name}
                                </span>
                                <span className="text-[11px] text-slate-500">
                                  {asset.asset_id} • {asset.category} • {asset.condition} • {asset.serial_number} • {asset.status}  
                                </span>
                              </button>
                            );
                          })}
                        {assets.filter(
                          (asset) =>
                            (asset.status === 'Available' || asset.asset_id === formData.asset_id) &&
                            (!selectedAssetPickerCategory ||
                              asset.category === selectedAssetPickerCategory)
                        ).length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500">
                            {selectedAssetPickerCategory
                              ? 'No assets in this category'
                              : 'Select a category to see assets'}
                          </div>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                
                {selectedAssetDetails && (
                  <div className="mt-1 text-[11px] text-slate-700 space-y-0.5">
                    <p>
                      <span className="font-semibold">Name:</span>{' '}
                      <span className="font-medium">{selectedAssetDetails.asset_name}</span>
                      {selectedAssetDetails.brand && (
                        <>
                          {'  '}· <span className="font-semibold">Brand:</span>{' '}
                          {selectedAssetDetails.brand}
                        </>
                      )}
                      {'  '}<span className="font-semibold">Condition:</span>{' '}
                      {selectedAssetDetails.condition}
                      <p>{selectedAssetDetails.brand && (
                        <>
                         <span className="font-semibold">Serial Number:</span>{' '}
                          {selectedAssetDetails.serial_number}
                        </>
                      )}</p>
                      
                    </p>
                  </div>
                )}
              </div>
              {/* Charger handling for Laptop/Mobile */}
              {isLaptopOrMobile && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="charger_given">Charger</Label>
                    <Select
                      value={formData.charger_given ? 'yes' : 'no'}
                      onValueChange={(value) => {
                        const given = value === 'yes';
                        setFormData((prev) => ({
                          ...prev,
                          charger_given: given,
                          ...(given
                            ? {}
                            : {
                                charger_return_note: '',
                              }),
                        }));
                      }}
                    >
                      <SelectTrigger data-testid="assignment-charger-given-select">
                        <SelectValue placeholder="Select charger given" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.charger_given && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="charger_details">Charger Details</Label>
                        <Input
                          id="charger_details"
                          data-testid="assignment-charger-details-input"
                          placeholder="e.g. 65W USB-C Charger"
                          value={formData.charger_details}
                          onChange={(e) => setFormData({ ...formData, charger_details: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="assigned_date">Assigned Date</Label>
                <Input
                  id="assigned_date"
                  data-testid="assignment-date-input"
                  type="date"
                  value={formData.assigned_date}
                  onChange={(e) => setFormData({ ...formData, assigned_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="assign_condition">Assign Condition</Label>
                <Select
                  value={formData.assign_condition}
                  onValueChange={(value) => setFormData({ ...formData, assign_condition: value })}
                >
                  <SelectTrigger data-testid="assign-condition-select">
                    <SelectValue placeholder="Select condition (auto-filled from asset)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Good">Good</SelectItem>
                    <SelectItem value="Damaged">Damaged</SelectItem>
                    <SelectItem value="Needs Repair">Needs Repair</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Condition of asset when assigned (auto-filled from asset)</p>
              </div>
              {editingAssignment && (
                <div className="space-y-2">
                  <Label htmlFor="return_date">Return Date</Label>
                  <Input
                    id="return_date"
                    data-testid="assignment-return-date-input"
                    type="date"
                    value={formData.return_date}
                    onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                  />
                </div>
              )}
              
              {/* Asset Return Condition - Show only when Return Date is filled */}
              {hasReturnDate && (
                <div className="space-y-2">
                  <Label htmlFor="asset_return_condition">
                    Return Condition <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.asset_return_condition}
                    onValueChange={(value) => setFormData({ ...formData, asset_return_condition: value })}
                  >
                    <SelectTrigger data-testid="asset-return-condition-select">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Good">Good</SelectItem>
                      <SelectItem value="Damaged">Damaged</SelectItem>
                      <SelectItem value="Needs Repair">Needs Repair</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Condition of asset when returned</p>
                </div>
              )}

              {/* Show only while editing, and only after Return Condition */}
              {editingAssignment && isLaptopOrMobile && formData.charger_given && (
                <div className="space-y-2">
                  <Label htmlFor="charger_return_note">Charger Return</Label>
                  <Input
                    id="charger_return_note"
                    data-testid="assignment-charger-return-note-input"
                    placeholder="e.g. Returned with cable issue / Not returned"
                    value={formData.charger_return_note}
                    onChange={(e) => setFormData({ ...formData, charger_return_note: e.target.value })}
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="remarks">Remarks (Optional)</Label>
                <Input
                  id="remarks"
                  data-testid="assignment-remarks-input"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                />
              </div>
              
              

              {/* SIM Connection Details - Show only for Mobile assets */}
              {isMobileAsset && (
                <>
                  <div className="col-span-2 mt-6 pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-semibold text-[#0B1F3A] mb-4">SIM Connection Details (Optional)</h3>
                  </div>

                  {/* SIM Connection picker using popover (provider -> numbers), supports multiple SIMs */}
                  <div className="col-span-2 space-y-2">
                    <Label>Link Existing SIM Connections</Label>
                    <Popover open={simPopoverOpen} onOpenChange={setSimPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-between"
                        >
                          <span className="truncate text-left">
                            {(selectedSimConnectionIds?.length || 0) === 0
                              ? 'Select provider and SIM number(s)'
                              : `${selectedSimConnectionIds.length} SIM${selectedSimConnectionIds.length > 1 ? 's' : ''} selected`}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[520px] p-0" align="start">
                        <div className="text-xs sm:text-sm text-slate-600 px-3 py-2 border-b">
                          First choose a SIM provider on the left, then pick one or more mobile numbers on the right.
                        </div>
                        <div className="flex max-h-72">
                          {/* Providers column */}
                          <div className="w-1/3 border-r border-slate-200 bg-slate-50 overflow-y-auto">
                            {Array.from(
                              new Set(
                                simConnections
                                  .map((sim) => sim.sim_provider)
                                  .filter(Boolean)
                              )
                            ).map((provider) => (
                              <button
                                key={provider}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-xs sm:text-sm border-b last:border-b-0 transition-colors ${
                                  selectedSimProviderFilter === provider
                                    ? 'bg-[#0B1F3A] text-white'
                                    : 'hover:bg-slate-100'
                                }`}
                                onClick={() => setSelectedSimProviderFilter(provider)}
                              >
                                {provider}
                              </button>
                            ))}
                            {simConnections.length === 0 && (
                              <div className="px-3 py-2 text-xs text-slate-500">
                                No SIM connections available
                              </div>
                            )}
                          </div>

                          {/* SIM numbers list column */}
                          <div className="w-2/3 bg-white overflow-y-auto">
                            {simConnections
                              .filter(
                                (sim) =>
                                  !selectedSimProviderFilter ||
                                  sim.sim_provider === selectedSimProviderFilter
                              )
                              .map((sim) => {
                                const isSelected =
                                  Array.isArray(selectedSimConnectionIds) &&
                                  selectedSimConnectionIds.includes(sim.id);
                                return (
                                  <button
                                    key={sim.id}
                                    type="button"
                                    className={`w-full text-left px-3 py-2 text-xs sm:text-sm border-b last:border-b-0 flex flex-col transition-colors ${
                                      isSelected ? 'bg-emerald-50' : 'hover:bg-slate-50'
                                    }`}
                                    onClick={() => {
                                      setSelectedSimConnectionIds((prev) => {
                                        if (!Array.isArray(prev)) return [sim.id];
                                        return prev.includes(sim.id)
                                          ? prev.filter((id) => id !== sim.id)
                                          : [...prev, sim.id];
                                      });
                                    }}
                                  >
                                    <span className="font-medium">
                                      {isSelected ? '✓ ' : ''}
                                      {sim.sim_provider} • {sim.mobile_number}
                                    </span>
                                    <span className="text-[11px] text-slate-500">
                                      {sim.sim_type} • {sim.sim_ownership}
                                    </span>
                                  </button>
                                );
                              })}
                            {simConnections.filter(
                              (sim) =>
                                !selectedSimProviderFilter ||
                                sim.sim_provider === selectedSimProviderFilter
                            ).length === 0 && (
                              <div className="px-3 py-2 text-xs text-slate-500">
                                {selectedSimProviderFilter
                                  ? 'No available SIMs for this provider'
                                  : 'Select a provider to see available SIMs'}
                              </div>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {selectedSimDetailsForDisplay.length > 0 && (
                      <div className="mt-1 text-[11px] text-slate-700 space-y-0.5">
                        <p className="font-semibold">Selected SIMs:</p>
                        {selectedSimDetailsForDisplay.map((sim) => (
                          <p key={sim.id}>
                            {sim.sim_provider} • {sim.mobile_number} ({sim.sim_type} • {sim.sim_ownership})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* NOTE: Individual SIM details (provider, number, type, ownership, purpose)
                      are now managed via SIM Connections and not on the assignment form. */}
                </>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="assignment-form-submit" type="submit">
                {editingAssignment ? 'Update' : 'Create'} Assignment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Search Employee Assets</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                data-testid="search-input"
                placeholder="Search by name, ID, or department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button data-testid="search-submit-button" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-4">
                {searchResults.map((employee) => (
                  <div key={employee.employee_id} className="border border-border rounded-lg p-4" data-testid={`search-result-${employee.employee_id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{employee.full_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {employee.employee_id} • {employee.department} • {employee.designation}
                        </p>
                      </div>
                    </div>
                    {employee.assigned_assets && employee.assigned_assets.length > 0 ? (
                      <div className="mt-3">
                        <p className="text-sm font-medium mb-2">Assigned Assets:</p>
                        <div className="space-y-2">
                          {employee.assigned_assets.map((asset) => (
                            <div key={asset.assignment_id} className="bg-muted/30 rounded p-2 text-sm">
                              <p className="font-medium">{asset.asset_name}</p>
                              <p className="text-xs text-muted-foreground">
                                ID: {asset.asset_id} • Assigned: {formatDisplayDate(asset.assigned_date)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-2">No assets currently assigned</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Asset View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Asset Overview</DialogTitle>
          </DialogHeader>
          {viewingAsset && (
            <div className="space-y-6 py-4">
              {/* Asset Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#0B1F3A] border-b pb-2">Asset Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Asset ID</Label>
                    <p className="text-sm mt-1">{viewingAsset.asset_id}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Asset Name</Label>
                    <p className="text-sm mt-1">{viewingAsset.asset_name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Category</Label>
                    <p className="text-sm mt-1">{viewingAsset.category}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Brand</Label>
                    <p className="text-sm mt-1">{viewingAsset.brand}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      {viewingAsset.category?.toLowerCase() === 'mobile' ? 'IMEI 1' : 'Serial Number'}
                    </Label>
                    <p className="text-sm mt-1">{viewingAsset.serial_number || '-'}</p>
                  </div>
                  {viewingAsset.category?.toLowerCase() === 'mobile' && viewingAsset.imei_2 && (
                    <div>
                      <Label className="text-sm font-medium text-slate-600">IMEI 2</Label>
                      <p className="text-sm mt-1">{viewingAsset.imei_2}</p>
                    </div>
                  )}
                  {(viewingAsset.category?.toLowerCase() === 'laptop' || viewingAsset.category?.toLowerCase() === 'mobile') && (
                    <div>
                      <Label className="text-sm font-medium text-slate-600">Charger</Label>
                      <p className="text-sm mt-1">{viewingAsset.charger || '-'}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Condition</Label>
                    <p className="text-sm mt-1">
                      <span className="px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                        {viewingAsset.condition}
                      </span>
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Status</Label>
                    <p className="text-sm mt-1">
                      <span
                        className={`px-2 py-1 rounded-md text-xs font-medium ${
                          viewingAsset.status === 'Available'
                            ? 'bg-green-100 text-green-800'
                            : viewingAsset.status === 'Assigned'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {viewingAsset.status}
                      </span>
                    </p>
                  </div>
                  {viewingAsset.remarks && (
                    <div className="col-span-2">
                      <Label className="text-sm font-medium text-slate-600">Remarks</Label>
                      <p className="text-sm mt-1">{viewingAsset.remarks}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Assignment Information */}
              {viewingAsset.assignment && (
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-lg font-semibold text-[#0B1F3A] border-b pb-2">Assignment Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-600">Assignment ID</Label>
                      <p className="text-sm mt-1">{viewingAsset.assignment.assignment_id}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-600">Employee Name</Label>
                      <p className="text-sm mt-1">{viewingAsset.assignment.employee_name}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-600">Employee ID</Label>
                      <p className="text-sm mt-1">{viewingAsset.assignment.employee_id}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-600">Assigned Date</Label>
                      <p className="text-sm mt-1">{formatDisplayDate(viewingAsset.assignment.assigned_date)}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-600">Condition (when assigned)</Label>
                      <p className="text-sm mt-1">{viewingAsset.assignment.assign_condition || '-'}</p>
                    </div>
                    {viewingAsset.assignment.return_date && (
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Return Date</Label>
                        <p className="text-sm mt-1">{formatDisplayDate(viewingAsset.assignment.return_date)}</p>
                      </div>
                    )}
                    {viewingAsset.assignment.return_date && (
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Condition (when returned)</Label>
                        <p className="text-sm mt-1">{viewingAsset.assignment.asset_return_condition || '-'}</p>
                      </div>
                    )}
                    {viewingAsset.assignment.remarks && (
                      <div className="col-span-2">
                        <Label className="text-sm font-medium text-slate-600">Assignment Remarks</Label>
                        <p className="text-sm mt-1">{viewingAsset.assignment.remarks}</p>
                      </div>
                    )}
                  </div>

                  {/* SIM Connection Details (if Mobile asset) */}
                  {viewingAsset.category?.toLowerCase() === 'mobile' && (
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-lg font-semibold text-[#0B1F3A] border-b pb-2">SIM Connection Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {viewingAsset.assignment.sim_connections?.length > 0 ? (
                          <>
                            <div className="col-span-2">
                              <Label className="text-sm font-medium text-slate-600">Linked SIMs</Label>
                              <ul className="mt-1 space-y-2">
                                {viewingAsset.assignment.sim_connections.map((sim) => (
                                  <li key={sim.id} className="text-sm bg-slate-50 rounded px-3 py-2">
                                    <span className="font-medium">{sim.sim_provider} • {sim.mobile_number}</span>
                                    <span className="text-slate-600 ml-2">({sim.sim_type} • {sim.sim_ownership})</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </>
                        ) : (
                          <>
                            {viewingAsset.assignment.sim_provider && (
                              <div>
                                <Label className="text-sm font-medium text-slate-600">SIM Provider</Label>
                                <p className="text-sm mt-1">{viewingAsset.assignment.sim_provider}</p>
                              </div>
                            )}
                            {viewingAsset.assignment.sim_mobile_number && (
                              <div>
                                <Label className="text-sm font-medium text-slate-600">SIM Mobile Number</Label>
                                <p className="text-sm mt-1">{viewingAsset.assignment.sim_mobile_number}</p>
                              </div>
                            )}
                            {viewingAsset.assignment.sim_type && (
                              <div>
                                <Label className="text-sm font-medium text-slate-600">SIM Type</Label>
                                <p className="text-sm mt-1">{viewingAsset.assignment.sim_type}</p>
                              </div>
                            )}
                            {viewingAsset.assignment.sim_ownership && (
                              <div>
                                <Label className="text-sm font-medium text-slate-600">SIM Ownership</Label>
                                <p className="text-sm mt-1">{viewingAsset.assignment.sim_ownership}</p>
                              </div>
                            )}
                            {viewingAsset.assignment.sim_purpose && (
                              <div className="col-span-2">
                                <Label className="text-sm font-medium text-slate-600">SIM Purpose</Label>
                                <p className="text-sm mt-1">{viewingAsset.assignment.sim_purpose}</p>
                              </div>
                            )}
                            {!viewingAsset.assignment.sim_provider &&
                             !viewingAsset.assignment.sim_mobile_number &&
                             !viewingAsset.assignment.sim_type &&
                             !viewingAsset.assignment.sim_ownership &&
                             !viewingAsset.assignment.sim_purpose && (
                              <div className="col-span-2">
                                <p className="text-sm text-muted-foreground">No SIM connection details available</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LifecycleModal
        open={lifecycleOpen}
        onOpenChange={setLifecycleOpen}
        type="asset"
        identifier={lifecycleAssetId}
      />
    </div>
  );
}